const EARTH_RADIUS_NM = 3440.065;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function haversineNm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.asin(Math.min(1, Math.sqrt(a)));
  return EARTH_RADIUS_NM * c;
}

export function initialTrueTrack(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δλ = toRad(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return (θ * 180) / Math.PI < 0 ? (θ * 180) / Math.PI + 360 : (θ * 180) / Math.PI;
}

export function formatNm(nm: number): string {
  return `${Math.round(nm).toLocaleString("en-US")} NM`;
}

export function formatEte(totalMin: number): string {
  const min = Math.max(0, Math.round(totalMin));
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}+${String(m).padStart(2, "0")}`;
}

export function formatTrack(deg: number): string {
  return `${String(Math.round(deg) % 360).padStart(3, "0")}°T`;
}

export type LatLon = { lat: number; lon: number };

/**
 * Approximate NM from a point to the great-circle segment A→B.
 * Equirectangular local frame — good enough for a ~100 NM corridor filter.
 */
export function distancePointToSegmentNm(
  lat: number,
  lon: number,
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
): number {
  const midLat = (aLat + bLat) / 2;
  const kx = Math.cos(toRad(midLat)) * 60;
  const ky = 60;
  const ax = aLon * kx;
  const ay = aLat * ky;
  const bx = bLon * kx;
  const by = bLat * ky;
  const px = lon * kx;
  const py = lat * ky;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) return haversineNm(lat, lon, aLat, aLon);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const qLat = (ay + t * dy) / ky;
  const qLon = (ax + t * dx) / kx;
  return haversineNm(lat, lon, qLat, qLon);
}

export function pointInCorridorNm(
  point: LatLon,
  origin: LatLon,
  dest: LatLon,
  corridorNm: number,
): boolean {
  return (
    distancePointToSegmentNm(point.lat, point.lon, origin.lat, origin.lon, dest.lat, dest.lon) <=
    corridorNm
  );
}

function wrapLon180(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}

/**
 * Spherical interpolation A→B. Phase 1 filed-route line — not airways.
 * `steps` is the number of segments (point count is steps + 1).
 */
export function greatCirclePoints(a: LatLon, b: LatLon, steps = 32): LatLon[] {
  if (!Number.isFinite(a.lat) || !Number.isFinite(a.lon) || !Number.isFinite(b.lat) || !Number.isFinite(b.lon)) {
    return [];
  }
  const n = Math.max(1, Math.round(steps));
  const φ1 = toRad(a.lat);
  const λ1 = toRad(a.lon);
  const φ2 = toRad(b.lat);
  let λ2 = toRad(b.lon);
  let dLon = λ2 - λ1;
  if (dLon > Math.PI) λ2 -= Math.PI * 2;
  if (dLon < -Math.PI) λ2 += Math.PI * 2;
  const d =
    2 *
    Math.asin(
      Math.min(
        1,
        Math.sqrt(Math.sin((φ2 - φ1) / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin((λ2 - λ1) / 2) ** 2),
      ),
    );
  if (d < 1e-12) return [{ lat: a.lat, lon: a.lon }, { lat: b.lat, lon: b.lon }];
  const sinD = Math.sin(d);
  const out: LatLon[] = [];
  for (let i = 0; i <= n; i++) {
    const f = i / n;
    const A = Math.sin((1 - f) * d) / sinD;
    const B = Math.sin(f * d) / sinD;
    const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
    const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
    const z = A * Math.sin(φ1) + B * Math.sin(φ2);
    out.push({
      lat: (Math.atan2(z, Math.sqrt(x * x + y * y)) * 180) / Math.PI,
      lon: wrapLon180((Math.atan2(y, x) * 180) / Math.PI),
    });
  }
  return out;
}

/** GeoJSON ring is `[lon, lat][]`. Ray-cast; holes are caller-handled. */
export function pointInRing(lat: number, lon: number, ring: ReadonlyArray<readonly [number, number]>): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]?.[0];
    const yi = ring[i]?.[1];
    const xj = ring[j]?.[0];
    const yj = ring[j]?.[1];
    if (xi == null || yi == null || xj == null || yj == null) continue;
    const intersect = yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi || Number.EPSILON) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function pointInPolygonRings(
  lat: number,
  lon: number,
  rings: ReadonlyArray<ReadonlyArray<readonly [number, number]>>,
): boolean {
  const outer = rings[0];
  if (!outer || !pointInRing(lat, lon, outer)) return false;
  for (let i = 1; i < rings.length; i++) {
    const hole = rings[i];
    if (hole && pointInRing(lat, lon, hole)) return false;
  }
  return true;
}

type GeoJsonGeometry = {
  type?: string;
  coordinates?: unknown;
};

function asLonLatRing(raw: unknown): Array<[number, number]> {
  if (!Array.isArray(raw)) return [];
  const out: Array<[number, number]> = [];
  for (const pair of raw) {
    if (!Array.isArray(pair) || pair.length < 2) continue;
    const lon = Number(pair[0]);
    const lat = Number(pair[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    out.push([lon, lat]);
  }
  return out;
}

/** Point-in-polygon for GeoJSON Polygon / MultiPolygon. Fail-soft on junk. */
export function pointInGeoJsonGeometry(lat: number, lon: number, geometry: GeoJsonGeometry | null | undefined): boolean {
  if (!geometry || !Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  if (geometry.type === "Polygon") {
    const rings = Array.isArray(geometry.coordinates) ? geometry.coordinates.map(asLonLatRing) : [];
    return pointInPolygonRings(lat, lon, rings);
  }
  if (geometry.type === "MultiPolygon") {
    if (!Array.isArray(geometry.coordinates)) return false;
    for (const poly of geometry.coordinates) {
      const rings = Array.isArray(poly) ? poly.map(asLonLatRing) : [];
      if (pointInPolygonRings(lat, lon, rings)) return true;
    }
  }
  return false;
}
